import type { TrackingPropsInterface } from '@marriott/mi-ui-library';
import { RESLINK } from '@marriott/mi-localstorage-utils';
import type {
  RetargetingSlotTarget,
  SlotContent,
  SlotTrackingContext,
} from '../../organisms/DynamicTabbedLayout/DynamicTabbedLayout.types';
import { isRecord } from '../helper';
import { EMPTY_RENDITIONS } from '../imageUtils';
import { buildCardTrackingDescription, trackingProps } from '../polarisTrackingUtils';
import {
  HOMEPAGE_RETARGETING_BADGE_ICON,
  HOMEPAGE_RETARGETING_BADGE_TEXT,
  HOMEPAGE_RETARGETING_DESCRIPTION,
  HOMEPAGE_HWS_RLM_USE_CASE,
} from './retargeting.constants';
import type { HomepageRetargetingCandidate, RetargetingProductDetailsCardProps } from './retargeting.types';

const VALID_CONTENT_TYPES = ['offers', 'property', 'destination', 'rnb'] as const;
const VALID_CARD_VARIATIONS = ['tall', 'square', 'wide'] as const;

export function normalizeSlotOrder(order: unknown): number | null {
  if (order === null || order === undefined) return null;
  const normalized = Number(order);
  return Number.isInteger(normalized) && normalized > 0 ? normalized : null;
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

function isValidRetargetingImageContext(value: unknown): boolean {
  if (!isRecord(value)) return false;
  if (!isNonEmptyString(value['candidateTimestamp']) || !isNonEmptyString(value['pageViewId'])) return false;
  if (normalizeSlotOrder(value['slotOrder']) === null) return false;
  if (value['suppliedImageUrl'] !== undefined && !isNonEmptyString(value['suppliedImageUrl'])) return false;

  if (value['useCase'] === HOMEPAGE_HWS_RLM_USE_CASE) return isNonEmptyString(value['marshaCode']);
  if (value['useCase'] === RESLINK) return value['marshaCode'] === undefined;
  return false;
}

export function isRetargetingPayload(value: unknown): value is RetargetingProductDetailsCardProps {
  if (!isRecord(value)) return false;

  return (
    VALID_CONTENT_TYPES.includes(value['contentType'] as (typeof VALID_CONTENT_TYPES)[number]) &&
    VALID_CARD_VARIATIONS.includes(value['cardVariation'] as (typeof VALID_CARD_VARIATIONS)[number]) &&
    isNonEmptyString(value['cardUrl']) &&
    typeof value['openInaNewTab'] === 'boolean' &&
    isNonEmptyString(value['title']) &&
    typeof value['description'] === 'string' &&
    typeof value['eyebrow'] === 'string' &&
    typeof value['imageAltText'] === 'string' &&
    isRecord(value['trackingProperties']) &&
    normalizeSlotOrder(value['index']) !== null &&
    isValidRetargetingImageContext(value['retargetingImageContext'])
  );
}

/**
 * Click-tracking description tail (WEB-207387): the applied use case. ResLink → Group;
 * HWS/RLM candidates are split by their resolved CTA — a dated deep link is the RLM
 * path, the flexible-date link is the ADF path.
 */
export function resolveRetargetingUseCaseTail(candidate: HomepageRetargetingCandidate): string {
  if (candidate.useCase === RESLINK) return 'Group';
  return candidate.targetUrl.includes('flexibleDateSearch=false') ? 'Property-RLM' : 'Property-ADF';
}

export function buildRetargetingProps(
  candidate: HomepageRetargetingCandidate,
  trackingCtx: SlotTrackingContext,
  pageViewId = 'standalone',
  authoredTracking?: Pick<RetargetingSlotTarget, 'clickTrack' | 'impressionTrack' | 'impressionCount'>
): RetargetingProductDetailsCardProps {
  const index = normalizeSlotOrder(trackingCtx.index) ?? 1;
  const cardVariation = trackingCtx.slotSize === 'banner' ? 'wide' : trackingCtx.slotSize;
  // clickTrack / impressionTrack / impressionCount come from the authored retargetingTilesList
  // entry; unauthored defaults are clickTrack on and impressions off (impressions ride the
  // N-ACDL PAGEVIEW work).
  const derivedTracking = trackingProps(
    { title: candidate.displayName, ...authoredTracking },
    { ...trackingCtx, index },
    'retargeting'
  );
  const trackingProperties = {
    ...derivedTracking,
    // WEB-207387 click format: HP-Card-Tab{N}-{TabName}-Card{M}-{Shape}-Retargeting-{TabName}-{UseCaseTail},
    // where the tail is the applied use case (Group | Property-RLM | Property-ADF), not card content.
    trackingDescription: buildCardTrackingDescription({
      tabIndex: trackingCtx.tabIndex ?? 1,
      tabName: trackingCtx.tabName ?? '',
      cardNumber: index,
      shape: trackingCtx.slotSize ?? 'square',
      category: 'Retargeting',
      description: trackingCtx.tabName ?? '',
      cta: resolveRetargetingUseCaseTail(candidate),
    }),
    isDCAEnabled: false,
  } as TrackingPropsInterface;

  const retargetingImageContext =
    candidate.useCase === HOMEPAGE_HWS_RLM_USE_CASE
      ? {
          useCase: candidate.useCase,
          marshaCode: candidate.marshaCode,
          candidateTimestamp: candidate.timestamp,
          pageViewId,
          slotOrder: index,
          ...(candidate.imageUrl ? { suppliedImageUrl: candidate.imageUrl } : {}),
        }
      : {
          useCase: candidate.useCase,
          candidateTimestamp: candidate.timestamp,
          pageViewId,
          slotOrder: index,
          ...(candidate.imageUrl ? { suppliedImageUrl: candidate.imageUrl } : {}),
        };

  return {
    contentType: 'property',
    cardVariation,
    cardUrl: candidate.targetUrl,
    openInaNewTab: false,
    eyebrow: candidate.eyebrow,
    badgeText: HOMEPAGE_RETARGETING_BADGE_TEXT,
    badgeIcon: HOMEPAGE_RETARGETING_BADGE_ICON,
    title: candidate.displayName,
    description: HOMEPAGE_RETARGETING_DESCRIPTION,
    fileReferenceImage: candidate.imageUrl || undefined,
    retargetingImageContext,
    imageAltText: `${candidate.displayName} recently viewed property image`,
    dynamicMedia: EMPTY_RENDITIONS,
    dynamicMediaWide: EMPTY_RENDITIONS,
    dynamicMediaTall: EMPTY_RENDITIONS,
    trackingProperties,
    breakPointKey: 'viewportL',
    index,
  };
}

export function buildRetargetingCfFallbackSlot(slot: SlotContent): SlotContent | null {
  if (slot.cfFallback == null) return null;
  return { ...slot, type: 'cf', payload: slot.cfFallback, cfFallback: null };
}

export function buildRetargetingCandidateKey(props: RetargetingProductDetailsCardProps): string {
  const context = props.retargetingImageContext;
  return [
    context.useCase,
    context.useCase === HOMEPAGE_HWS_RLM_USE_CASE ? context.marshaCode : '',
    context.slotOrder,
    context.candidateTimestamp,
    context.suppliedImageUrl ?? '',
    context.pageViewId,
    props.cardUrl,
  ].join('|');
}

export function resolveRetargetingSlot(
  slot: SlotContent,
  candidate: HomepageRetargetingCandidate | null,
  trackingCtx: SlotTrackingContext,
  pageViewId: string
): SlotContent {
  const targetOrder = normalizeSlotOrder(slot.retargetingTarget?.order);
  if (targetOrder === null || targetOrder !== normalizeSlotOrder(slot.position)) return slot;
  if (!candidate) {
    return {
      ...slot,
      type: 'cf',
      payload: slot.cfFallback ?? slot.payload ?? null,
    };
  }

  return {
    ...slot,
    type: 'retargeting',
    payload: buildRetargetingProps(
      candidate,
      { ...trackingCtx, index: targetOrder },
      pageViewId,
      slot.retargetingTarget
    ),
  };
}
